import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { API_LINKS_TOKENIZE } from '@/api/endpoints';
import type { ExternalLink, TokenizeUrlResponse } from '@/types/links';
import { unwrapAxiosResponse } from '@/utils/axios';
import { ErrorMessage, logRequestError } from '@/utils/errors';

const openInNewTab = (url: string) =>
  window.open(url, '_blank', 'noopener,noreferrer');

export const useOpenExternalLink = (onError?: () => void) => {
  const { mutateAsync: tokenizeUrl } = useMutation({
    mutationFn: (url: string) =>
      apiClient
        .post<TokenizeUrlResponse>(API_LINKS_TOKENIZE, { url })
        .then(unwrapAxiosResponse),
  });

  return async (link: ExternalLink) => {
    if (!link.isSingleSignOn) {
      openInNewTab(link.url);
      return;
    }

    const newTab = window.open('', '_blank');

    try {
      const { url } = await tokenizeUrl(link.url);

      if (newTab) {
        newTab.opener = null;
        newTab.location.href = url;
        return;
      }

      openInNewTab(url);
    } catch (error) {
      newTab?.close();
      logRequestError(ErrorMessage.TokenizeExternalLink, error);
      onError?.();
    }
  };
};
